import { FirebaseError } from 'firebase/app';

/**
 * GRO-959 — which auth failures justify signing the user out.
 *
 * `onSignOut()` ends in `window.location.href = RouterPaths.signIn`: a full page
 * reload that tears down every provider. Firing it on a *recoverable* token
 * failure restarts any in-flight onboarding — the reload lands on the public
 * sign-in route, Firebase re-resolves, and AuthMiddleware's public-route bounce
 * sends the user back through /dashboard to /onboarding with their in-memory
 * wizard state gone. A paying customer lost 48 minutes to that loop.
 *
 * Only a credential the server has actually rejected justifies that reset.
 * `auth/network-request-failed` was already handled this way in the token
 * handler's switch; these codes are the ones that genuinely require signing in
 * again.
 */
export const TERMINAL_AUTH_ERROR_CODES = new Set<string>([
  'auth/invalid-user-token',
  'auth/user-token-expired',
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/invalid-credential',
  'auth/requires-recent-login',
]);

/**
 * Unknown and non-Firebase failures are treated as recoverable on purpose: the
 * next `onIdTokenChanged` tick retries, whereas a wrong sign-out costs the user
 * their session and their onboarding progress.
 */
export function isTerminalAuthError(error: unknown): boolean {
  return (
    error instanceof FirebaseError && TERMINAL_AUTH_ERROR_CODES.has(error.code)
  );
}

/**
 * BUG-800 — Firebase's answers for a password that does not open the account:
 * the wrong password, and an address with no account at all (`user-not-found`,
 * or `invalid-credential` when email enumeration protection hides which one).
 * An invited teammate who never activated has no account, so after one of
 * these the sign-in page asks whether the address is invited. Any other code
 * (`too-many-requests`, `network-request-failed`...) says nothing about the
 * password and keeps the alert of today.
 */
export const REJECTED_PASSWORD_CODES: ReadonlySet<string> = new Set([
  'auth/invalid-credential',
  'auth/wrong-password',
  'auth/invalid-login-credentials',
  'auth/user-not-found',
]);
